import { useCallback, useEffect, useRef } from 'react';
import LogicFlow from '@logicflow/core';

interface UseRightClickSelectionOptions {
  /** 是否启用右键框选功能 */
  enabled?: boolean;
  /** 框选灵敏度：true 表示只要碰到节点就选中，false 表示需要完全包含节点 */
  isWholeNode?: boolean;
  /** 框选完成后的回调 */
  onSelectionComplete?: (selectedNodeIds: string[]) => void;
}

interface SelectionBox {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

/**
 * 右键框选 hook
 * 用于在画布上通过右键拖拽框选多个节点
 */
export const useRightClickSelection = (
  lfRef: React.MutableRefObject<LogicFlow | null>,
  canvasRef: React.RefObject<HTMLDivElement>,
  options: UseRightClickSelectionOptions = {}
) => {
  const { enabled = true, isWholeNode = false, onSelectionComplete } = options;

  // 框选状态
  const isSelectingRef = useRef(false);
  const selectionBoxRef = useRef<SelectionBox | null>(null);
  const selectionElementRef = useRef<HTMLDivElement | null>(null);

  /**
   * 创建框选 DOM 元素
   */
  const createSelectionElement = useCallback(() => {
    if (selectionElementRef.current) return selectionElementRef.current;

    const element = document.createElement('div');
    element.className = 'right-click-selection-box';
    element.style.cssText = `
      position: fixed;
      border: 2px dashed #1890ff;
      background-color: rgba(24, 144, 255, 0.1);
      pointer-events: none;
      z-index: 9998;
      display: none;
    `;
    document.body.appendChild(element);
    selectionElementRef.current = element;
    return element;
  }, []);

  /**
   * 更新框选元素位置和大小
   */
  const updateSelectionElement = useCallback((box: SelectionBox) => {
    const element = selectionElementRef.current;
    if (!element) return;

    const left = Math.min(box.startX, box.endX);
    const top = Math.min(box.startY, box.endY);
    const width = Math.abs(box.endX - box.startX);
    const height = Math.abs(box.endY - box.startY);

    element.style.left = `${left}px`;
    element.style.top = `${top}px`;
    element.style.width = `${width}px`;
    element.style.height = `${height}px`;
    element.style.display = 'block';
  }, []);

  /**
   * 隐藏框选元素
   */
  const hideSelectionElement = useCallback(() => {
    const element = selectionElementRef.current;
    if (element) {
      element.style.display = 'none';
    }
  }, []);

  /**
   * 将屏幕坐标转换为画布坐标
   */
  const screenToCanvas = useCallback(
    (screenX: number, screenY: number) => {
      const lf = lfRef.current;
      if (!lf) return { x: screenX, y: screenY };

      try {
        const point = lf.getPointByClient(screenX, screenY) as any;
        if (point?.canvasOverlayPosition) {
          return {
            x: point.canvasOverlayPosition.x,
            y: point.canvasOverlayPosition.y,
          };
        }
        if (point?.domOverlayPosition) {
          return {
            x: point.domOverlayPosition.x,
            y: point.domOverlayPosition.y,
          };
        }
        return { x: point?.x ?? screenX, y: point?.y ?? screenY };
      } catch {
        return { x: screenX, y: screenY };
      }
    },
    [lfRef]
  );

  /**
   * 检查节点是否在框选范围内
   */
  const isNodeInSelectionBox = useCallback(
    (
      nodeModel: any,
      boxStartCanvas: { x: number; y: number },
      boxEndCanvas: { x: number; y: number }
    ) => {
      const nodeX = nodeModel.x;
      const nodeY = nodeModel.y;
      const nodeWidth = nodeModel.width || 100;
      const nodeHeight = nodeModel.height || 50;

      // 节点边界
      const nodeLeft = nodeX - nodeWidth / 2;
      const nodeRight = nodeX + nodeWidth / 2;
      const nodeTop = nodeY - nodeHeight / 2;
      const nodeBottom = nodeY + nodeHeight / 2;

      // 框选边界
      const boxLeft = Math.min(boxStartCanvas.x, boxEndCanvas.x);
      const boxRight = Math.max(boxStartCanvas.x, boxEndCanvas.x);
      const boxTop = Math.min(boxStartCanvas.y, boxEndCanvas.y);
      const boxBottom = Math.max(boxStartCanvas.y, boxEndCanvas.y);

      if (isWholeNode) {
        // 需要完全包含节点
        return (
          nodeLeft >= boxLeft &&
          nodeRight <= boxRight &&
          nodeTop >= boxTop &&
          nodeBottom <= boxBottom
        );
      } else {
        // 只要相交就选中
        return (
          nodeLeft < boxRight &&
          nodeRight > boxLeft &&
          nodeTop < boxBottom &&
          nodeBottom > boxTop
        );
      }
    },
    [isWholeNode]
  );

  /**
   * 执行框选，选中框内的节点
   * 注意：如果子节点的父分组也被选中，则不单独选中子节点，避免双重移动
   */
  const performSelection = useCallback(() => {
    const lf = lfRef.current;
    const box = selectionBoxRef.current;
    if (!lf || !box) return;

    // 将屏幕坐标转换为画布坐标
    const startCanvas = screenToCanvas(box.startX, box.startY);
    const endCanvas = screenToCanvas(box.endX, box.endY);

    // 获取所有节点
    const nodes = lf.graphModel.nodes;
    const nodesInBox: string[] = [];

    // 第一步：收集所有在框选范围内的节点
    nodes.forEach((nodeModel: any) => {
      if (isNodeInSelectionBox(nodeModel, startCanvas, endCanvas)) {
        nodesInBox.push(nodeModel.id);
      }
    });

    // 第二步：过滤掉父分组也被选中的子节点，避免双重移动
    const nodesInBoxSet = new Set(nodesInBox);
    const selectedNodeIds: string[] = [];

    nodesInBox.forEach(nodeId => {
      const nodeModel = lf.getNodeModelById(nodeId) as any;
      if (!nodeModel) return;

      // 检查是否是分组内的子节点
      const parentGroupId = nodeModel.properties?.parentGroupId;
      const isChildOfGroup =
        nodeModel.properties?.isState || nodeModel.properties?.isValue;

      // 如果是子节点且其父分组也在框选范围内，则跳过（不单独选中）
      if (isChildOfGroup && parentGroupId && nodesInBoxSet.has(parentGroupId)) {
        console.log(
          `⏭️ 跳过子节点 ${nodeId}，其父分组 ${parentGroupId} 也被选中`
        );
        return;
      }

      // 检查是否是 DynamicGroup 的子节点
      if (nodeModel.group) {
        const groupId = nodeModel.group;
        if (nodesInBoxSet.has(groupId)) {
          console.log(
            `⏭️ 跳过分组子节点 ${nodeId}，其所属分组 ${groupId} 也被选中`
          );
          return;
        }
      }

      selectedNodeIds.push(nodeId);
    });

    // 选中节点
    if (selectedNodeIds.length > 0) {
      // 先清除之前的选中
      lf.clearSelectElements();

      // 选中框选范围内的所有节点（已排除子节点）
      selectedNodeIds.forEach(nodeId => {
        lf.selectElementById(nodeId, true); // 第二个参数 true 表示多选
      });

      console.log(
        `✅ 右键框选完成，选中 ${selectedNodeIds.length} 个节点:`,
        selectedNodeIds
      );

      // 触发回调
      onSelectionComplete?.(selectedNodeIds);
    } else {
      console.log('ℹ️ 框选范围内没有节点');
    }
  }, [lfRef, screenToCanvas, isNodeInSelectionBox, onSelectionComplete]);

  /**
   * 处理右键按下事件
   */
  const handleContextMenu = useCallback((e: MouseEvent) => {
    // 阻止默认右键菜单
    e.preventDefault();
  }, []);

  /**
   * 处理鼠标按下事件
   */
  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (!enabled) return;

      // 只处理右键
      if (e.button !== 2) return;

      // 检查是否点击在画布区域
      const canvas = canvasRef.current;
      if (!canvas) return;

      const canvasRect = canvas.getBoundingClientRect();
      if (
        e.clientX < canvasRect.left ||
        e.clientX > canvasRect.right ||
        e.clientY < canvasRect.top ||
        e.clientY > canvasRect.bottom
      ) {
        return;
      }

      // 开始框选
      isSelectingRef.current = true;
      selectionBoxRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        endX: e.clientX,
        endY: e.clientY,
      };

      // 创建并显示框选元素
      createSelectionElement();
      updateSelectionElement(selectionBoxRef.current);

      console.log('🔲 开始右键框选');
    },
    [enabled, canvasRef, createSelectionElement, updateSelectionElement]
  );

  /**
   * 处理鼠标移动事件
   */
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isSelectingRef.current || !selectionBoxRef.current) return;

      // 更新框选范围
      selectionBoxRef.current.endX = e.clientX;
      selectionBoxRef.current.endY = e.clientY;

      // 更新框选元素显示
      updateSelectionElement(selectionBoxRef.current);
    },
    [updateSelectionElement]
  );

  /**
   * 处理鼠标释放事件
   */
  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      if (!isSelectingRef.current) return;

      // 只处理右键释放
      if (e.button !== 2) return;

      // 更新最终位置
      if (selectionBoxRef.current) {
        selectionBoxRef.current.endX = e.clientX;
        selectionBoxRef.current.endY = e.clientY;

        // 执行框选
        performSelection();
      }

      // 重置状态
      isSelectingRef.current = false;
      selectionBoxRef.current = null;
      hideSelectionElement();

      console.log('🔲 结束右键框选');
    },
    [performSelection, hideSelectionElement]
  );

  // 设置事件监听器
  useEffect(() => {
    if (!enabled) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    // 在画布上监听右键菜单事件（阻止默认菜单）
    canvas.addEventListener('contextmenu', handleContextMenu);

    // 全局监听鼠标事件（因为鼠标可能移出画布）
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      canvas.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      // 清理框选元素
      if (selectionElementRef.current) {
        selectionElementRef.current.remove();
        selectionElementRef.current = null;
      }
    };
  }, [
    enabled,
    canvasRef,
    handleContextMenu,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  ]);

  return {
    /** 当前是否正在框选 */
    isSelecting: isSelectingRef.current,
  };
};

export default useRightClickSelection;
